import {
    PropertyDescription,
    MutatingScreenKind,
    InputOutputTables,
    TableGlideType,
    getPrimitiveColumns,
    DefaultableComponentDescription,
} from "../description";
import { ComponentHandlerBase } from "./handler";
import { ComponentDescriptor, ComponentBuilder, ComponentConfiguratorContext, PropertyDescriptor } from ".";
import { getDocURL } from "../../docUrl";
import { makeTextPropertyDescriptor, makeDefaultValuePropertyDescriptor } from "./descriptor-utils";
import { SerializedComponent } from "../../components/SerializedApp";
import { definedMap } from "@glideapps/ts-necessities";
import { PropertySection } from "./property-sections";
import { setColumnInRowForEditComponent, getDefaultValuesForComponent, makeText } from "../builder-utils";
import { ColumnPropertyHandler, ColumnPropertyFlag, isRequiredPropertyHandler } from "./property-handlers";
import { getFeatureSetting } from "../../feature-settings";

const ComponentKindSignature = "signature";

const imagePropertyHandler = new ColumnPropertyHandler(
    "imageURLProperty",
    "Column",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable, ColumnPropertyFlag.EditedInApp],
    undefined,
    ["signature"],
    getPrimitiveColumns,
    "image-uri",
    PropertySection.Data
);

interface SignatureDescription extends DefaultableComponentDescription {
    readonly titleProperty: PropertyDescription | undefined;
    readonly messageProperty: PropertyDescription | undefined;
    readonly placeholderProperty: PropertyDescription | undefined;
    readonly imageURLProperty: PropertyDescription;
}

export class SignatureComponentHandler extends ComponentHandlerBase<SignatureDescription> {
    constructor() {
        super(ComponentKindSignature);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public needValidation(): boolean {
        return true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: SignatureDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const imageUrlProperty = imagePropertyHandler.getColumnName(desc);
        if (imageUrlProperty === undefined) {
            builder.error("URL property for image is not a column");
            return b.emptyUI;
        }

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            imageUrlProperty,
            b.getProperty(imageUrlProperty, outputContext, tables.output),
            undefined,
            tables.output,
            undefined,
            b.getContext,
            { debounce: false }
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const title = makeText(builder, desc.titleProperty, tables.input, true);
        const placeholder = makeText(builder, desc.placeholderProperty, tables.input, true);
        const message = makeText(builder, desc.messageProperty, tables.input, true);
        const isRequired = isRequiredPropertyHandler.getSwitch(desc);

        return b.let(
            lets,
            b.ui("SignatureField", {
                placeholder: placeholder ?? b.localizedString("addSignature"),
                title: title ?? b.localizedString("signature"),
                imageURL: valueToModify,
                onChange: action,
                onTap: builder.makePushSignaturePadScreen(
                    message ?? b.localizedString("signatureMessage"),
                    valueToModify,
                    definedMap(action, a => b.bindContextToValue(a, b.getContext)),
                    validFlag
                ),
                isEnabled,
                isRequired: b.constant(isRequired),
                validFlag,
            })
        );
    }

    public getDescriptor(
        _desc: SignatureDescription | undefined,
        _table: TableGlideType | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<SignatureDescription> {
        const properties: PropertyDescriptor[] = [
            makeTextPropertyDescriptor("titleProperty", "Title", "Signature", false, mutatingScreenKind, {
                searchable: false,
                emptyByDefault: true,
                propertySection: PropertySection.Design,
            }),
            makeTextPropertyDescriptor("placeholderProperty", "Hint text", "Add signature", false, mutatingScreenKind, {
                searchable: false,
                emptyByDefault: true,
                propertySection: PropertySection.Design,
            }),
            makeTextPropertyDescriptor(
                "messageProperty",
                "Message",
                "Sign using your finger",
                false,
                mutatingScreenKind,
                {
                    searchable: false,
                    emptyByDefault: true,
                    propertySection: PropertySection.Design,
                }
            ),
            imagePropertyHandler,
            ...this.getBasePropertyDescriptors(),
        ];

        if (mutatingScreenKind !== undefined) {
            properties.push(isRequiredPropertyHandler);

            const defaultDescr = makeDefaultValuePropertyDescriptor(ccc, mutatingScreenKind, "image-uri", "Enter URL");
            if (defaultDescr !== undefined) {
                properties.push(defaultDescr);
            }
        }

        return {
            name: "Signature",
            description: "Lets the app user collect signatures",
            img: "signature",
            group: "Entry Fields",
            helpUrl: getDocURL("signature"),
            isLegacy: !getFeatureSetting("signatureComponent"),
            quotaWarning: ccc.eminenceFlags.proComponents ? undefined : "Enter 10 signatures per month on free apps.",
            properties,
        };
    }

    public getDefaultValues(
        builder: ComponentBuilder,
        desc: SignatureDescription,
        { input }: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        return getDefaultValuesForComponent(builder, [desc.imageURLProperty, desc.defaultValue], input, "image-uri");
    }
}
